import { useQuery } from '@apollo/client';
import { CircularProgress, Grid, Paper, Tooltip } from '@mui/material';
import { Grouping, Module } from '@thro-types/modules-selections';
import { ShibUser, User } from '@thro-types/users';

import { t } from 'i18next';
import { ReactNode, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import LATEST_LOTTERY_QUERY from 'gql/queries/ModuleSelectionsService/getLatestLotteryBySelection.gql';

import { useLatestCalculatedLotteryResults } from 'api/hooks/module-selection';
import { useModulesQuery } from 'api/hooks/modules';
import { TypedGridColumns } from 'app/components';
import { DataGrid } from 'app/components/DataGrid';
import GQLGuard from 'app/components/GQLGuard';
import { ColouredInputBox } from 'app/components/RichList/items/AWPM-FWPM/ModuleItem/components/ColouredInputBox';
import { Lottery } from 'app/containers/AWPM-FWPM/StudentView/models';
import { userDataToLecturerOption } from 'app/containers/AWPM-FWPM/helper';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import { useCurrentURL } from 'utils/hooks/useCurrentURL';

import { SubjectType, ModuleSelection } from '../../models';
import { PreCalculationControl } from '../components/PreCalculationControl';

type Props = {
  moduleSelection: ModuleSelection;
  subjectType: SubjectType;
  isSelectionAdmin: boolean;
  currentUser?: User;
};

type GetLatestLottery = { getLatestLotteryBySelection: Lottery };

export function AllocationTab({
  moduleSelection,
  subjectType,
  isSelectionAdmin,
  currentUser
}: Props) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentURL = useCurrentURL();

  const getLatestLottery = useQuery<GetLatestLottery>(LATEST_LOTTERY_QUERY, {
    variables: { moduleSelectionId: Number(moduleSelection.id) },
    context: { clientName: 'federation' }
  });

  const {
    data: latestCalculatedLotteryResults,
    isLoading: isLatestCalculatedLotteryResultsLoading
  } = useLatestCalculatedLotteryResults(Number(moduleSelection.id), {
    params: { withDischarged: false }
  });

  const { data: modules, isLoading } = useModulesQuery({
    params: {
      selectionId: moduleSelection.id,
      lecturerId: !!currentUser && !isSelectionAdmin ? currentUser.id.toString() : undefined,
      nameSort: 'asc'
    }
  });

  const getNumberOfWishlistedModule = useCallback(
    (moduleId: number): ReactNode => {
      if (isLatestCalculatedLotteryResultsLoading) {
        return <CircularProgress size={20} />;
      }
      if (!latestCalculatedLotteryResults) {
        return '-';
      }
      const module = latestCalculatedLotteryResults.modules.find(m => m.moduleId === moduleId);
      if (module) {
        // Check if user has already recieved desired number of modules
        return module.waitlist.filter(user => !user.receivedDesiredNumberOfModules).length;
      }
      return '-';
    },
    [latestCalculatedLotteryResults, isLatestCalculatedLotteryResultsLoading]
  );

  const getNumberOfParticipantsInModule = useCallback(
    (moduleId: number): ReactNode => {
      if (isLatestCalculatedLotteryResultsLoading) {
        return <CircularProgress size={20} />;
      }
      if (!latestCalculatedLotteryResults) {
        return '-';
      }
      const module = latestCalculatedLotteryResults.modules.find(
        (m: { moduleId: number }) => m.moduleId === moduleId
      );
      if (module) {
        return module.assigned.length;
      }
      return '-';
    },
    [latestCalculatedLotteryResults, isLatestCalculatedLotteryResultsLoading]
  );

  const columns = useMemo((): TypedGridColumns<
    Module & {
      countParticipants: number;
      countWaitinglist: number;
      grouping: Grouping;
      moduleOfficerUser: ShibUser;
    }
  > => {
    return [
      {
        flex: 3,
        minWidth: 200,
        headerName: t(translations.pages.modules.create.modulePreview.title()),
        field: 'name',
        renderCell: params => (
          <Tooltip title={params.row.name}>
            <span>{params.row.name}</span>
          </Tooltip>
        )
      },
      {
        flex: 2,
        minWidth: 400,
        headerName: t(translations.pages.modules.grouping()),
        field: 'grouping',
        valueGetter: (value, row) => row.grouping?.name,
        renderCell: params => (
          <ColouredInputBox
            text={params.row.grouping?.name || ''}
            color="#48A2D1"
            backgroundColor="#C1E6F7"
          />
        )
      },
      {
        flex: 2,
        minWidth: 200,
        headerName: t(translations.pages.modules.moduleResponsible()),
        field: 'moduleOfficerUser',
        renderCell: params =>
          !!params.row.moduleOfficerUser ? (
            <Tooltip title={userDataToLecturerOption(params.row.moduleOfficerUser, t).label}>
              <span>{userDataToLecturerOption(params.row.moduleOfficerUser, t).label}</span>
            </Tooltip>
          ) : (
            <></>
          )
      },
      {
        flex: 2,
        minWidth: 100,
        headerName: t(translations.pages.moduleSelections.allocation.listHeaders.minParticipants()),
        field: 'minParticipants',
        renderCell: params => <span>{params.row.minParticipants}</span>
      },
      {
        flex: 2,
        minWidth: 100,
        headerName: t(translations.pages.moduleSelections.allocation.listHeaders.maxParticipants()),
        field: 'maxParticipants',
        renderCell: params => <span>{params.row.maxParticipants}</span>
      },
      {
        flex: 2,
        minWidth: 100,
        headerName: t(
          translations.pages.moduleSelections.allocation.listHeaders.countParticipants()
        ),
        field: 'countParticipants',
        renderCell: params => <span>{getNumberOfParticipantsInModule(params.row.id)}</span>
      },
      {
        flex: 2,
        minWidth: 100,
        headerName: t(
          translations.pages.moduleSelections.allocation.listHeaders.countWaitinglist()
        ),
        field: 'countWaitinglist',
        renderCell: params => <span>{getNumberOfWishlistedModule(params.row.id)}</span>
      }
    ];
  }, [getNumberOfParticipantsInModule, getNumberOfWishlistedModule]);

  return (
    <GQLGuard
      query={getLatestLottery}
      onError={error => {
        if (error?.graphQLErrors[0]?.extensions?.status === 404) {
          dispatch(AlertActions.error(`${t(translations.pages.modules.alerts.failedLoading())}`));
        } else {
          dispatch(
            AlertActions.error(`${t(translations.pages.modules.alerts.failedLoading())}: ${error}`)
          );
        }
        navigate('/', { replace: true });
        return null;
      }}
    >
      {(latestLotteryData: GetLatestLottery) => (
        <Grid container>
          <PreCalculationControl
            moduleSelection={moduleSelection}
            latestLotteryData={latestLotteryData.getLatestLotteryBySelection}
          />
          <Grid container>
            <Grid
              item
              flexGrow={1}
              style={{ height: `70vh`, width: '100%' }}
              component={Paper}
              padding={2}
            >
              <DataGrid
                style={{ height: '100%', width: '100%' }}
                disableColumnFilter
                disableRowSelectionOnClick
                columns={columns}
                onRowClick={event => {
                  navigate(`/${subjectType.toLowerCase()}/modules/${event.row.id}`, {
                    state: { back: currentURL }
                  });
                }}
                rows={modules || []}
                paginationMode={'client'}
                columnVisibilityModel={{ grouping: subjectType !== SubjectType.Fwpm }}
                loading={isLoading}
              />
            </Grid>
          </Grid>
        </Grid>
      )}
    </GQLGuard>
  );
}
